import React, { useState } from "react";
import axios from "axios";

import {
    Box,
    Button,
    Paper,
    Typography,
    LinearProgress,
    Alert,
    Stack,
    Grid,
    Divider,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";

import { getCookie } from "../../../helpers/cookies";

function BulkPhotoDownloaderContainer() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];

        setError("");
        setSuccess("");

        if (!file) {
            setSelectedFile(null);
            return;
        }

        const fileName = file.name.toLowerCase();

        if (!fileName.endsWith(".xlsx")) {
            setError("Please select an Excel .xlsx file.");
            setSelectedFile(null);
            return;
        }

        // 10 MB limit
        if (file.size > 10 * 1024 * 1024) {
            setError("File size must be less than 10 MB.");
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);
    };

    const handleDownload = async () => {
        if (!selectedFile) {
            setError("Please select an Excel file first.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setSuccess("");

            const formData = new FormData();

            formData.append("file", selectedFile);

            const token = getCookie("asmc_token");

            if (!token) {
                setError("Authentication token not found. Please log in again.");
                return;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/member-photo-download/upload`,
                formData,
                {
                    responseType: "blob",
                    headers: {
                        Authorization: `BEARER ${token}`,
                    },
                }
            );

            const blob = new Blob([response.data], {
                type: "application/zip",
            });

            const downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download = "member-photos.zip";

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(downloadUrl);

            setSuccess("Member photos downloaded successfully.");
        } catch (err) {
            console.error("Bulk photo download error:", err);

            let errorMessage = "Failed to download member photos.";

            try {
                if (err.response?.data instanceof Blob) {
                    const text = await err.response.data.text();

                    try {
                        const data = JSON.parse(text);

                        if (data?.message) {
                            errorMessage = data.message;
                        }
                    } catch {
                        // Response was not JSON
                    }
                } else if (err.response?.data?.message) {
                    errorMessage = err.response.data.message;
                }
            } catch (parseError) {
                console.error("Error parsing API error:", parseError);
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack spacing={2.5}>
            {/* Page Header */}
            <Paper
                elevation={0}
                sx={{
                    px: 3,
                    py: 2.2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    background:
                        "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 46,
                            height: 46,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "primary.main",
                            color: "white",
                        }}
                    >
                        <PhotoLibraryIcon />
                    </Box>

                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: "text.primary",
                            }}
                        >
                            Bulk Member Photo Downloader
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Upload an Excel file to download member photos in bulk.
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Main Card */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                }}
            >
                {/* Instructions */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        mb: 0.5,
                    }}
                >
                    Upload Member List
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2.5 }}
                >
                    Upload an Excel file containing the member IDs whose
                    photos you want to download.
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* Upload Area */}
                <Box
                    sx={{
                        border: "2px dashed",
                        borderColor: selectedFile
                            ? "success.main"
                            : "grey.300",
                        borderRadius: 2,
                        p: { xs: 3, sm: 4 },
                        textAlign: "center",
                        backgroundColor: selectedFile
                            ? "success.50"
                            : "#fafafa",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "#f8fbff",
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            mx: "auto",
                            mb: 1.5,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: selectedFile
                                ? "success.100"
                                : "primary.50",
                            color: selectedFile
                                ? "success.main"
                                : "primary.main",
                        }}
                    >
                        {selectedFile ? (
                            <CheckCircleIcon fontSize="medium" />
                        ) : (
                            <UploadFileIcon fontSize="medium" />
                        )}
                    </Box>

                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            mb: 0.5,
                        }}
                    >
                        {selectedFile
                            ? "Excel file selected"
                            : "Select your Excel file"}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        .xlsx format only • Maximum file size: 10 MB
                    </Typography>

                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        disabled={loading}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        {selectedFile ? "Choose Another File" : "Select Excel File"}

                        <input
                            type="file"
                            hidden
                            accept=".xlsx"
                            onChange={handleFileChange}
                        />
                    </Button>
                </Box>

                {/* Selected File */}
                {selectedFile && (
                    <Box
                        sx={{
                            mt: 2.5,
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 1.2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "success.50",
                                    color: "success.main",
                                    flexShrink: 0,
                                }}
                            >
                                <InsertDriveFileIcon />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Selected File
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {selectedFile.name}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {(selectedFile.size / 1024).toFixed(2)} KB
                                </Typography>
                            </Box>
                        </Box>

                        <CheckCircleIcon
                            color="success"
                            sx={{ flexShrink: 0 }}
                        />
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mt: 2.5,
                            borderRadius: 1.5,
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Success */}
                {success && (
                    <Alert
                        severity="success"
                        sx={{
                            mt: 2.5,
                            borderRadius: 1.5,
                        }}
                        icon={<CheckCircleIcon />}
                    >
                        {success}
                    </Alert>
                )}

                {/* Progress */}
                {loading && (
                    <Box
                        sx={{
                            mt: 2.5,
                            p: 2,
                            borderRadius: 1.5,
                            backgroundColor: "primary.50",
                            border: "1px solid",
                            borderColor: "primary.100",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                            >
                                Preparing member photos...
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Please wait
                            </Typography>
                        </Box>

                        <LinearProgress
                            sx={{
                                height: 6,
                                borderRadius: 3,
                            }}
                        />
                    </Box>
                )}

                {/* Download Button */}
                <Grid container justifyContent="flex-end">
                    <Grid item>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<DownloadIcon />}
                            disabled={!selectedFile || loading}
                            onClick={handleDownload}
                            sx={{
                                mt: 3,
                                px: 3.5,
                                py: 1.2,
                                borderRadius: 1.5,
                                textTransform: "none",
                                fontWeight: 700,
                                boxShadow: "none",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            {loading
                                ? "Preparing ZIP..."
                                : "Download Photos"}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Stack>
    );
}

export default BulkPhotoDownloaderContainer;